import { basicUIPlugin } from '@stackflow/plugin-basic-ui'
import { basicRendererPlugin } from '@stackflow/plugin-renderer-basic'
import { ActivityComponentType, stackflow } from '@stackflow/react'
import { GetServerSideProps } from 'next'
import { useMemo } from 'react'
import AIAccuracyActivity from '../../../../components/webview/conversational-ai/AI/accuracy/AIAccuracyActivity'
import AIAccuracyMetricDetailActivity from '../../../../components/webview/conversational-ai/AI/accuracy/AIAccuracyMetricDetailActivity'
import AILevelDescriptionActivity from '../../../../components/webview/conversational-ai/AI/common/AILevelDescriptionActivity'
import AIRingleTipActivity from '../../../../components/webview/conversational-ai/AI/common/AIRingleTipActivity'
import AIComplexityActivity from '../../../../components/webview/conversational-ai/AI/complexity/AIComplexityActivity'
import AIComplexityMetricDetailActivity from '../../../../components/webview/conversational-ai/AI/complexity/AIComplexityMetricDetailActivity'
import AIFluencyActivity from '../../../../components/webview/conversational-ai/AI/fluency/AIFluencyActivity'
import AIHighlightDetailActivity from '../../../../components/webview/conversational-ai/AI/highlights/AIHighlightDetailActivity'
import AIOverviewHighlightListActivity from '../../../../components/webview/conversational-ai/AI/highlights/AIOverviewHighlightListActivity'
import AIPronunciationActivity from '../../../../components/webview/conversational-ai/AI/pronunciation/AIPronunciationActivity'
import ConversationAnalysisActivity from '../../../../components/webview/conversational-ai/ConversationAnalysisActivity'
import ConversationCompleteActivity from '../../../../components/webview/conversational-ai/ConversationCompleteActivity'
import ConversationInstructionActivity from '../../../../components/webview/conversational-ai/ConversationInstructionActivity'
import ConversationListActivity from '../../../../components/webview/conversational-ai/ConversationListActivity'
import ConversationMainActivity from '../../../../components/webview/conversational-ai/ConversationMainActivity'
import ConversationNotificationActivity from '../../../../components/webview/conversational-ai/ConversationNotificationActivity'

const activities = {
  ConversationListActivity,
  ConversationMainActivity,
  ConversationInstructionActivity,
  ConversationCompleteActivity,
  ConversationAnalysisActivity,
  ConversationNotificationActivity,
  AIComplexityActivity,
  AIAccuracyActivity,
  AIFluencyActivity,
  AIPronunciationActivity,
  AILevelDescriptionActivity,
  AIComplexityMetricDetailActivity,
  AIAccuracyMetricDetailActivity,
  AIHighlightDetailActivity,
  AIRingleTipActivity,
  AIOverviewHighlightListActivity,
}

type SameKeyValue<T extends keyof typeof activities> = {
  [K in T]: K
}

const createActivitiesMap = <T extends keyof typeof activities>(
  acts: Record<T, ActivityComponentType>
): SameKeyValue<T> =>
  (Object.keys(acts) as T[])
    .map((activity) => ({ [activity]: activity }))
    .reduce((ac, cr) => {
      return {
        ...ac,
        ...cr,
      }
    }, {}) as SameKeyValue<T>

export const CONVERSATION_ACTIVITIES_MAP = createActivitiesMap(activities)

export type ConversationAIActivityNamesType = keyof typeof activities

export const useConversationAIStack = (initialActivityName?: string | string[], initialParams?) => {
  const {
    Stack: ConversationAIStack,
    useFlow: useConversationAIFlow,
    activities: ConversationAIActivities,
  } = useMemo(() => {
    return stackflow({
      transitionDuration: 350,
      activities: activities,
      plugins: [
        basicRendererPlugin(),
        basicUIPlugin({
          theme: 'cupertino',
        }),
        () => {
          return {
            key: 'query-param-nested-navigation-plugin',
            overrideInitialEvents: ({ initialEvents }) => {
              const temp = {
                ...initialEvents[0],
                activityName: initialActivityName as ConversationAIActivityNamesType,
                activityParams: initialParams,
              }
              return [temp]
            },
          }
        },
      ],
      initialActivity: () => initialActivityName as ConversationAIActivityNamesType,
    })
  }, [initialActivityName])

  return { ConversationAIStack, ConversationAIActivities, useConversationAIFlow }
}

const Index = ({ activity, threadId }) => {
  const { ConversationAIStack } = useConversationAIStack(activity, { threadId: threadId })
  return (
    <div className="h-full w-full ">
      <ConversationAIStack />
    </div>
  )
}

export const getServerSideProps: GetServerSideProps = async (props) => {
  const { query } = props
  const { activity = 'ConversationListActivity', threadId = '' } = query
  return {
    props: {
      activity: activity,
      threadId: threadId,
    },
  }
}

export default Index
